import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Empty, Spin, Tag } from 'antd';
import { PROMOTION_SCHEDULE_LOG_OPERATION } from '../../../../../lib/api/promotionSchedule';
import { promotionScheduleQuery } from '../../queries';
import { formatKst } from '../../utils/kst';
import { LOG_OPERATION_LABEL, buildLogEntries, formatLogPeriod } from '../../utils/logDiff';
import { StyledChangeRow, StyledEntry, StyledEntryHead, StyledLogList, StyledSpinWrap } from './styles';

type Props = {
  promotionScheduleId: number;
};

const OPERATION_COLOR: Record<string, string> = {
  [PROMOTION_SCHEDULE_LOG_OPERATION.CREATE]: 'green',
  [PROMOTION_SCHEDULE_LOG_OPERATION.UPDATE]: 'blue',
  [PROMOTION_SCHEDULE_LOG_OPERATION.DELETE]: 'red',
};

/**
 * 변경 이력 목록.
 *
 * 서버는 스냅샷만 주고 diff 는 `buildLogEntries` 가 인접 비교로 만든다.
 * 등록 건은 비교 대상이 없어 변경 내역이 비고, 그때는 "최초 등록" 문구로 대체한다.
 */
export const ScheduleLogList = (props: Props): React.JSX.Element => {
  const { promotionScheduleId } = props;

  const { data: logs = [], isLoading, isError } = useQuery(promotionScheduleQuery.logs(promotionScheduleId));

  if (isLoading) {
    return (
      <StyledSpinWrap>
        <Spin />
      </StyledSpinWrap>
    );
  }

  /**
   * 실패와 "이력이 없음"을 구분한다 — 둘 다 빈 배열이라 같은 문구를 쓰면
   * 조회가 깨진 걸 이력이 없는 것으로 오해한다.
   */
  if (isError) {
    return <Empty description="변경 이력을 불러오지 못했습니다." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  if (logs.length === 0) {
    return <Empty description="변경 이력이 없습니다." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const entries = buildLogEntries(logs);

  return (
    <StyledLogList>
      {entries.map(({ log, changes }) => (
        <StyledEntry key={log.id}>
          <StyledEntryHead>
            <Tag color={OPERATION_COLOR[log.operation]}>{LOG_OPERATION_LABEL[log.operation] ?? log.operation}</Tag>
            <span className="log-admin">{log.adminName ?? '스크립트/백필'}</span>
            {/* 기간(formatLogPeriod)이 KST 고정이므로 등록일시도 같은 기준이어야 한 카드 안에서 시각이 어긋나지 않는다 */}
            <span className="log-date">{formatKst(log.createDate, 'yyyy-MM-dd HH:mm')}</span>
          </StyledEntryHead>

          {log.operation === PROMOTION_SCHEDULE_LOG_OPERATION.CREATE ? (
            <StyledChangeRow>
              <span className="change-field">최초 등록</span>
              <span className="change-value">
                {log.title} · {formatLogPeriod(log)}
              </span>
            </StyledChangeRow>
          ) : changes.length === 0 ? (
            <StyledChangeRow>
              <span className="change-field">변경 내역 없음</span>
            </StyledChangeRow>
          ) : (
            changes.map((change) => (
              <StyledChangeRow key={change.field}>
                <span className="change-field">{change.field}</span>
                <span className="change-before">{change.before}</span>
                <span className="change-arrow">→</span>
                <span className="change-after">{change.after}</span>
              </StyledChangeRow>
            ))
          )}
        </StyledEntry>
      ))}
    </StyledLogList>
  );
};
